import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import TextField from '../forms/TextField';
import { validEmail, required } from '../forms/validations';

const VALID_EMAIL_REGEX = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
import { requestExtraSearches } from './backend';
import { dispatchEvent } from '../../shared/events';
import { getPlanByPriceId, getBenefitsForPlan, getMonthlyForTier } from '../../shared/plans';
import sharedCookies from '../../shared/sharedCookies';
import useReCAPTCHA from './useReCAPTCHA';
import './ContinueAccessMenu.scss';

const FREE_BENEFITS = [
  <><i className='fa-solid fa-check'></i>Keyword volumes &amp; competition data</>,
  <><i className='fa-solid fa-check'></i>Keywords driving traffic to competitors</>,
  <><i className='fa-solid fa-check'></i>Longtail keyword suggestions</>,
];

export default function ContinueAccessMenu({ user, onClose }) {

  const [Email, SetEmail] = useState('');
  const [Error, SetError] = useState(null);
  const [Processing, SetProcessing] = useState(false);
  const [ButtonState, SetButtonState] = useState('idle');

  const plan = getPlanByPriceId('bronze_trial_plan');
  const benefits = getBenefitsForPlan(plan);
  const price = plan.unit_amount_in_cents / 100;
  const hasCoupon = !!sharedCookies.get('coupon');

  const {
    ReCAPTCHAComponent,
    getReCAPTCHAToken,
  } = useReCAPTCHA(__WORDTRACKER_INVISIBLE_RECAPTCHA_SITE_KEY);

  useEffect(() => {
    if (ButtonState === 'already_claimed') {
      const timer = setTimeout(() => SetButtonState('idle'), 3000);
      return () => clearTimeout(timer);
    }
  }, [ButtonState]);

  const handleSubmit = async () => {
    if (!Email || !VALID_EMAIL_REGEX.test(String(Email).toLowerCase())) {
      SetError('Must be a valid email address');
      return;
    }
    SetProcessing(true);
    SetError(null);
    try {
      const token = await getReCAPTCHAToken();
      await requestExtraSearches(token, Email);
      SetButtonState('success');
    } catch (error) {
      if (error?.errors === 'already claimed') {
        SetButtonState('already_claimed');
      } else {
        SetError(error?.errors || 'Something went wrong. Please try again.');
      }
    } finally {
      SetProcessing(false);
    }
  };

  const handleUnlock = () => {
    if (hasCoupon) {
      const monthlyPlan = getMonthlyForTier(plan.tier);
      window.location.href = `${__ZUUL_URL}/sign-up/details?plan=${monthlyPlan.lookup_key}`;
    } else {
      onClose();
      setTimeout(() => dispatchEvent('requestedSubscribe'), 200);
    }
  };

  const handleChange = (e) => {
    SetEmail(e.target.value);
    if (Error) SetError(null);
  };
  const handleErrorStateChange = (_e, error) => SetError(error);
  const isDisabled = Processing;

  const buttonClass = [
    Processing ? 'processing' : '',
    ButtonState === 'already_claimed' ? 'error' : '',
    ButtonState === 'success' ? 'success' : '',
  ].join(' ').trim();

  const buttonContent = () => {
    if (Processing) return <><i className='fa-solid fa-spinner fa-spin'></i> Processing...</>;
    if (ButtonState === 'success') return <><i className='fa-solid fa-check'></i> Please check your email</>;
    if (ButtonState === 'already_claimed') return <><i className='fa-solid fa-xmark'></i> Sorry, email already claimed</>;
    return <>Get {__THROTTLING_VERIFIED_LIMIT} more searches <i className='fa-solid fa-arrow-right'></i></>;
  };

  return (
    <div className='sales-modal-container continue-access-menu'>
      {ReactDOM.createPortal(ReCAPTCHAComponent, document.body)}

      <div className='continue-access-grid'>

        <div className='continue-access-left'>
          <div className='continue-access-left-content'>
            <span className='sales-modal-plan-label'>Continue your search</span>

            <div className='sales-modal-stage-header'>
              <h2>Get {__THROTTLING_VERIFIED_LIMIT} more free searches</h2>
              <p>See real keyword data for {__THROTTLING_VERIFIED_LIMIT} more searches — no card needed</p>
            </div>

            <ul className='sales-modal-benefits'>
              {FREE_BENEFITS.map((benefit, i) => (
                <li key={i}>{benefit}</li>
              ))}
            </ul>

            <div className='continue-access-email-form'>
              <div className='sales-modal-field'>
                <TextField
                  name='verify-address' type='text' label='' klassname='email'
                  initialValue={Email} onErrorStateChange={handleErrorStateChange}
                  onChange={handleChange} error={Error}
                  validations={[required(), validEmail()]} placeholder='name@example.com'
                  data-lpignore='true' autoComplete='off'
                />
              </div>
              <button
                type='button'
                className={`sales-modal-button ${buttonClass}`}
                disabled={isDisabled && ButtonState !== 'success'}
                onClick={handleSubmit}
              >{buttonContent()}</button>
              <p className='sales-modal-hint'>We'll also send you a few helpful keyword tips. Unsubscribe anytime. <a href='/privacy'>Privacy Policy</a></p>
            </div>
          </div>
        </div>

        <div className='continue-access-divider'>
          <span>OR</span>
        </div>

        <div className='continue-access-right'>
          <div className='continue-access-right-content'>
            <span className='sales-modal-plan-label'>Full access</span>

            <div className='sales-modal-stage-header'>
              <h2>Full access</h2>
            </div>

            {hasCoupon && (
              <div className='continue-access-coupon'>
                <i className='fa-regular fa-gift'></i>
                <span>You have a discount waiting to be applied!</span>
              </div>
            )}

            {!hasCoupon && (
              <div className='price'>
                <span className='amount'>${price}</span>
                <span className='period'>/month</span>
              </div>
            )}

            <ul className='sales-modal-benefits'>
              {benefits.map(({ label }, i) => (
                <li key={i}><i className='fa-solid fa-check'></i>{label}</li>
              ))}
            </ul>

            <div className='continue-access-cta'>
              <button
                type='button'
                className='continue-access-unlock-button'
                onClick={handleUnlock}
              >{hasCoupon ? 'Redeem your discount' : `Unlock everything for $${price}`}</button>
              <p className='sales-modal-hint'>{hasCoupon ? 'Your discount will be applied at checkout.' : 'Cancel anytime.'}</p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
